import {
  useEffect,
  useMemo,
  useState,
  createContext,
  useCallback,
  useContext,
} from "react";
import {
  getChartRouteState,
  useChartLocationAndPageMode,
} from "./Hook/useChartLocationAndPageMode";
import { useChartCreateTicketFlow } from "./Hook/useChartCreateTicketFlow";
import { useChartCreateOrderFlow } from "./Hook/useChartCreateOrderFlow";
import { useLocation, useHistory } from "react-router-dom";
import MainContext from "../MainContext/MainContext";
import MUIDialog from "@mui/material/Dialog";
import MUIDialogTitle from "@mui/material/DialogTitle";
import MUIDialogContent from "@mui/material/DialogContent";
import MUIDialogActions from "@mui/material/DialogActions";
import MUIButton from "@mui/material/Button";
import MUIBackdrop from "@mui/material/Backdrop";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

import api from "../../Seating/Security/api";
import Validator from "../../Utility/Validator";
import Progress from "../../Component/Progress";
import RuleEngine from "../../Utility/RuleEngine/RuleEngine";
import Setting from "../../Utility/Setting";

export const ChartContext = createContext();

/**
 * ChartContextProvider
 * @param {Object} children - The children components
 * @returns {JSX.Element}
 */
export default function ChartContextProvider({ children }) {
  const ORDER_PAGE_MODES = ["new", "resupply", "accessoryAfterSale"];
  const TICKET_PAGE_MODES = ["service", "billingIssue", "pickup"];
  const CHILD_ORDER_PAGE_MODES = ["accessoryAfterSale"];
  const location = useLocation();
  const history = useHistory();
  const mainContext = useContext(MainContext);
  const cloneChartData = useCallback(
    (chartData) =>
      chartData ? JSON.parse(JSON.stringify(chartData)) : chartData,
    [],
  );
  const [originalChart, setOriginalChart] = useState(null);
  const [chart, setChart] = useState(null);
  const [draftOrderState, setDraftOrderState] = useState(null);
  const [validationIssues, setValidationIssues] = useState({});
  const [cardsInEditMode, setCardsInEditMode] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [validationRules, setValidationRules] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showManageSignatureDialog, setShowManageSignatureDialog] =
    useState(false);
  const [pendingSave, setPendingSave] = useState(null);
  const [pendingNavigatePath, setPendingNavigatePath] = useState(null);
  const [ruleViolations, setRuleViolations] = useState({});
  const [createTicketValidationRules, setCreateTicketValidationRules] =
    useState({});
  const [createTicketValidationIssues, setCreateTicketValidationIssues] =
    useState({});
  const [pendingCreateTicket, setPendingCreateTicket] = useState(false);
  const [chartViews, setChartViews] = useState([]);
  const [selectedChartViewId, setSelectedChartViewId] = useState(
    Setting.get("chart.selectedChartViewId") || 0,
  );
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [requestedScrollToCard, setRequestedScrollToCard] = useState(null);
  const [insuranceDiscoveryActiveStep, setInsuranceDiscoveryActiveStep] =
    useState(0);
  const [insuranceDiscoveryApiResults, setInsuranceDiscoveryApiResults] =
    useState(null);
  const [insuranceDiscoverySelectedUser, setInsuranceDiscoverySelectedUser] =
    useState(null);
  const [insuranceDiscoverySelectedKey, setInsuranceDiscoverySelectedKey] =
    useState(null);
  const [
    insuranceDiscoverySelectedPatientIndex,
    setInsuranceDiscoverySelectedPatientIndex,
  ] = useState(null);
  const [insuranceDiscoveryCoverageTypes, setInsuranceDiscoveryCoverageTypes] =
    useState({}); // { rowKey: "primary" | "secondary" }
  const [
    insuranceDiscoveryInsuranceOverrides,
    setInsuranceDiscoveryInsuranceOverrides,
  ] = useState({}); // { rowKey: { insuranceTypeId, insuranceSubTypeId } } when no single Payor ID match
  const [insuranceDiscoveryIsLoading, setInsuranceDiscoveryIsLoading] =
    useState(false);
  const [
    insuranceDiscoveryAddressChanges,
    setInsuranceDiscoveryAddressChanges,
  ] = useState(null);

  const routeState = useMemo(
    () =>
      getChartRouteState(location.pathname, [
        ...TICKET_PAGE_MODES,
        ...CHILD_ORDER_PAGE_MODES,
      ]),
    [location.pathname],
  );
  const patientId = routeState.patientId;
  const pageMode = routeState.pageMode;
  const draftOrderId = useMemo(() => {
    if (!draftOrderState?.id) {
      return null;
    }

    if ((draftOrderState.pageMode ?? null) !== (pageMode ?? null)) {
      return null;
    }

    if (
      (draftOrderState.parentOrderId ?? null) !==
      (routeState.parentOrderId ?? null)
    ) {
      return null;
    }

    return draftOrderState.id;
  }, [draftOrderState, pageMode, routeState.parentOrderId]);
  const orderId = routeState.orderId ?? draftOrderId;
  const patient = chart?.patient[patientId];

  const setDraftOrderIdForCurrentRoute = useCallback(
    (nextOrderId) => {
      if (nextOrderId == null) {
        setDraftOrderState(null);
        return;
      }

      setDraftOrderState({
        id: nextOrderId,
        pageMode: pageMode ?? null,
        parentOrderId: routeState.parentOrderId ?? null,
      });
    },
    [pageMode, routeState.parentOrderId],
  );

  /**
   * Formats the orderType so we store it correctly in the database.
   */
  const formatOrderType = useCallback((orderType) => {
    switch (orderType) {
      case "resupply":
        return "Re-Supply";
      case "billingIssue":
        return "Billing Issue";
      case "accessoryAfterSale":
        return "Accessory After Sale";
      default:
        return orderType
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
    }
  }, []);

  /**
   * Handles chart changes by merging updatedChart into the current chart
   * state. Assumes structure: table => id => object (object only contains
   * primitives).
   * @param {Object} updatedChart - The chart data to update.
   */
  const onChange = useCallback((updatedChart) => {
    setChart((prevChart) => {
      const newChart = { ...prevChart };
      for (const table in updatedChart) {
        newChart[table] = { ...prevChart[table] };
        for (const id in updatedChart[table]) {
          const updatedEntry = updatedChart[table][id];
          if (updatedEntry === undefined) {
            delete newChart[table][id];
          } else {
            newChart[table][id] = {
              ...(prevChart[table]?.[id] || {}),
            };
            for (const key in updatedEntry) {
              if (updatedEntry[key] === undefined) {
                delete newChart[table][id][key]; // remove specific key
              } else {
                newChart[table][id][key] = updatedEntry[key];
              }
            }
          }
        }
      }
      return newChart;
    });
  }, []);

  /**
   * Updates validation rules for a specific key.
   * Accepts a string key and an array of rule objects.
   * Each rule object should have: target, id, field, and rule.
   * @param {Object} params - Parameters object.
   * @param {string} params.key - The grouping key for the rules.
   * @param {Array} params.rules - Array of rule objects.
   */
  const setCardEditMode = useCallback((cardName, isEditMode) => {
    setCardsInEditMode((prev) => {
      const wasInEditMode = prev[cardName] === true;
      if (wasInEditMode === Boolean(isEditMode)) {
        return prev;
      }

      const next = { ...prev };
      if (isEditMode) {
        next[cardName] = true;
      } else {
        delete next[cardName];
      }
      return next;
    });
  }, []);

  /**
   * Updates the validation rules state.
   * @param {Object} params - The parameters object.
   * @param {string} params.key - The key to update.
   * @param {Array} params.rules - The rules to update.
   */
  const updateValidationRules = useCallback(({ key, rules }) => {
    setValidationRules((prev) => ({
      ...prev,
      [key]: rules,
    }));
  }, []);

  /**
   * Updates the validation issues state.
   * @param {Object} issues - The validation issues object.
   */
  const updateValidationIssues = useCallback((issues) => {
    setValidationIssues(issues);
  }, []);

  /**
   * Toggles the expanded state for a given order row.
   * @param {string|number} orderId - The order id to expand/collapse.
   */
  const updateExpandedRow = useCallback((orderId) => {
    setExpandedRows((prev) => ({
      [orderId]: !prev[orderId],
    }));
  }, []);

  /**
   * Toggles the show manage signature dialog state.
   * @param {boolean} show - The show state.
   */
  const updateShowManageSignatureDialog = useCallback((show) => {
    setShowManageSignatureDialog(show);
  }, []);

  /**
   * Updates the insurance discovery active step.
   * @param {number} step - The step to update to.
   */
  const updateInsuranceDiscoveryActiveStep = useCallback((step) => {
    setInsuranceDiscoveryActiveStep(step);
  }, []);

  /**
   * Updates the insurance discovery is loading state.
   * @param {boolean} isLoading - The is loading state.
   */
  const updateInsuranceDiscoveryIsLoading = useCallback((isLoading) => {
    setInsuranceDiscoveryIsLoading(isLoading);
  }, []);

  /**
   * Updates the insurance discovery api results.
   * @param {Object} data - The api results.
   */
  const updateInsuranceDiscoveryApiResults = useCallback((data) => {
    setInsuranceDiscoveryApiResults(data);
  }, []);

  /**
   * Updates the insurance discovery selected user.
   * @param {Object} user - The user/insurance to update to.
   * @param {string|null} key - The composite key (e.g. "0-0") to uniquely identify the row.
   */
  const updateInsuranceDiscoverySelectedUser = useCallback(
    (user, key = null) => {
      setInsuranceDiscoverySelectedUser(user);
      setInsuranceDiscoverySelectedKey(key);
    },
    [],
  );

  /**
   * Updates the insurance discovery selected patient index.
   * @param {number|null} index - The index of the selected patient in insuranceDiscoveryData.
   */
  const updateInsuranceDiscoverySelectedPatientIndex = useCallback((index) => {
    setInsuranceDiscoverySelectedPatientIndex(index);
  }, []);

  /**
   * Updates the insurance discovery insurance coverage types (primary & secondary per row).
   * @param {Object} roles - Map of rowKey to "primary" | "secondary".
   */
  const updateInsuranceDiscoveryCoverageTypes = useCallback((coverageTypes) => {
    setInsuranceDiscoveryCoverageTypes(coverageTypes);
  }, []);

  /**
   * Updates the insurance discovery insurance overrides (Ins Type + Payor) per row when no single Payor ID match.
   * @param {Object} overrides - Map of rowKey to { insuranceTypeId, insuranceSubTypeId }.
   */
  const updateInsuranceDiscoveryInsuranceOverrides = useCallback(
    (overrides) => {
      setInsuranceDiscoveryInsuranceOverrides(overrides);
    },
    [],
  );

  /**
   * Updates the insurance discovery address changes.
   * @param {Array} addressChanges - The address changes.
   */
  const updateInsuranceDiscoveryAddressChanges = useCallback(
    (addressChanges) => {
      setInsuranceDiscoveryAddressChanges(addressChanges);
    },
    [],
  );

  /**
   * Prepares the order data for the create order page.
   * @param {string} orderType - The type of order to create. "new" or "re-supply".
   */
  const applyLoaderChartFromLocation = useCallback(
    (routerLocation) => {
      if (routerLocation?.state?.loaderResult?.chart) {
        const loaderChart = cloneChartData(
          routerLocation.state.loaderResult.chart,
        );
        setOriginalChart(loaderChart);
        setChart(cloneChartData(loaderChart));
      }
    },
    [cloneChartData],
  );

  /**
   * Restores the chart state after a history pop.
   * @returns {void}
   */
  const restoreChartStateAfterHistoryPop = useCallback(() => {
    setChart(cloneChartData(originalChart));
    setValidationIssues({});
    setCreateTicketValidationRules({});
    setCreateTicketValidationIssues({});
    setValidationRules({});
    setRuleViolations({});
    setCardsInEditMode({});
    setPendingCreateTicket(false);
    setPendingNavigatePath(null);
    setPendingSave(null);
    window.salesPilot.negativeId = -1;
  }, [cloneChartData, originalChart]);

  /**
   * Gets the chart by patient id.
   * @param {number} patientIdToLoad - The patient id to load.
   * @returns {void}
   */
  useEffect(() => {
    if (originalChart || !patientId) {
      return;
    }

    getChartByPatientId(patientId);
  }, [originalChart, patientId]);

  /**
   * Handles the chart location and page mode.
   * @returns {void}
   */
  useChartLocationAndPageMode({
    history: history,
    location: location,
    originalChart: originalChart,
    onHistoryPopRestore: restoreChartStateAfterHistoryPop,
    applyLoaderChartFromLocation: applyLoaderChartFromLocation,
    setUploadedFile: setUploadedFile,
  });

  /**
   * Gracefully redirect back to the patient chart page if something bad
   * happens. Typically this would be reloading on the create ticket page
   * because it won't run the create ticket functions and thus order -1 won't
   * exist.
   */
  useEffect(() => {
    if (
      !isChartLoading &&
      !!chart &&
      !!orderId &&
      !!pageMode &&
      TICKET_PAGE_MODES.includes(pageMode) &&
      orderId < 0 &&
      !chart.orderActivity?.[orderId] &&
      !!patientId
    ) {
      history.push(`/chart/${patientId}`);
    }
  }, [chart, history, orderId, pageMode, patientId, isChartLoading]);

  /**
   * Gets a negative ID for a new object that does not yet have an id. Each
   * call returns a decremented negative number.
   * @returns {number} The negative ID
   */
  function getNegativeId() {
    if (window.salesPilot.negativeId === undefined) {
      window.salesPilot.negativeId = -1;
    }
    return window.salesPilot.negativeId--;
  }

  /**
   * Resets the chart state to the original chart state.
   * @param {Object} originalChart - The original chart to reset to.
   */
  const resetChart = useCallback(() => {
    setChart(cloneChartData(originalChart));
    setValidationIssues({});
    setCreateTicketValidationRules({});
    setCreateTicketValidationIssues({});
    setValidationRules({});
    window.salesPilot.negativeId = -1;
  }, [cloneChartData, originalChart]);

  /**
   * Reloads and saves chart data if there are changes.
   * @returns
   */
  async function reloadChartData() {
    const result = diffObjects(chart, originalChart);

    if (Object.keys(result).length > 0) {
      setPendingSave(true);
      setShowSaveDialog(true);
    } else {
      setIsUpdating(true);
      await getChart();
    }
    setIsUpdating(false);
    return;
  }

  async function getChartByPatientId(patientIdToLoad) {
    if (isChartLoading) {
      return;
    }
    setIsChartLoading(true);

    const response = await api.send("POST", "/chart/get", {
      patientId: parseInt(patientIdToLoad),
    });

    setOriginalChart(cloneChartData(response));
    setChart(cloneChartData(response));

    setIsChartLoading(false);
  }

  /**
   * Gets the chart.
   * @returns {void}
   */
  async function getChart() {
    if (!patientId) {
      return;
    }

    await getChartByPatientId(patientId);
  }

  function validateChartFields(validationRules) {
    // Run all of the validation
    let validationIssues = {};
    for (const [key, rules] of Object.entries(validationRules)) {
      for (const rule of rules) {
        const issues = Validator.validate(chart[rule.target]?.[rule.id], {
          [rule.field]: rule.rule,
        });
        if (Object.keys(issues).length > 0) {
          if (!validationIssues[key]) {
            validationIssues[key] = {};
          }
          if (!validationIssues[key][rule.target]) {
            validationIssues[key][rule.target] = {};
          }
          if (!validationIssues[key][rule.target][rule.id]) {
            validationIssues[key][rule.target][rule.id] = {};
          }
          Object.assign(validationIssues[key][rule.target][rule.id], issues);
        }
      }
    }

    setValidationIssues(validationIssues);
    return validationIssues;
  }

  /**
   * Diff two objects to determine what has changed between them.
   *
   * This function recursively compares object 'a' (original) and object 'b'
   * (updated), and returns an object representing only the changed fields.
   *
   * Special handling is performed for certain keys:
   * - For objects with numeric keys (e.g., orderActivity,
   *   orderActivityProducts), we treat them as maps of entities.
   * - For 'orderActivityProducts', we persist both 'orderActivityId' and
   *   'productsId' because the backend expects a compound key.
   * - For other entities, we persist the 'id' so the backend knows which
   *   entity is being updated.
   * - For 'orderActivity', we also persist 'lastUpdatedOn' to indicate the
   *   version being edited, which is used for optimistic concurrency control.
   *
   * @param {Object} a - The original object.
   * @param {Object} b - The updated object.
   * @returns {Object} The diff object containing only changed fields.
   */
  const diffObjects = useCallback(
    (a, b) => {
      const result = {};
      for (const key in b) {
        if (!(key in a)) {
          // Key is new in 'b', add it to the diff.
          result[key] = b[key];
        } else if (
          typeof b[key] === "object" &&
          b[key] !== null &&
          !Array.isArray(b[key])
        ) {
          // Recursively diff nested objects.
          const nestedDiff = diffObjects(a[key], b[key]);
          if (Object.keys(nestedDiff).length > 0) {
            // If all keys are numeric, treat as entity map (e.g., orderActivity, orderActivityProducts).
            if (Object.keys(b[key]).every((k) => !isNaN(parseInt(k)))) {
              result[key] = {};
              // Process each entity by ID.
              for (const id in b[key]) {
                if (nestedDiff[id]) {
                  // For orderActivityProducts, persist compound key for backend.
                  if (key === "orderActivityProducts") {
                    result[key][id] = {
                      orderActivityId: nestedDiff[id].orderActivityId,
                      productsId: nestedDiff[id].productsId,
                    };
                  } else if (key === "orderActivityApprovals") {
                    result[key][id] = {
                      orderActivityId: nestedDiff[id].orderActivityId,
                      approvalsId: nestedDiff[id].approvalsId,
                    };
                  } else {
                    // For other entities, persist 'id' so backend knows which entity to update.
                    result[key][id] = {
                      ...nestedDiff[id],
                      id: parseInt(id),
                    };
                    // For orderActivity, also persist 'lastUpdatedOn' for versioning.
                    if (key === "orderActivity" && a[key][id]?.lastUpdatedOn) {
                      result[key][id].lastUpdatedOn = a[key][id].lastUpdatedOn;
                    }
                  }
                }
              }
            } else {
              // Not an entity map, just persist the nested diff.
              result[key] = nestedDiff;
            }
          }
        } else if (JSON.stringify(b[key]) !== JSON.stringify(a[key])) {
          // Primitive value changed, add to diff.
          result[key] = b[key];
        }
      }
      return result;
    },
    [/* no dependencies */],
  );

  // Add hasPendingChanges state
  const hasPendingChanges = useMemo(() => {
    if (!originalChart || !chart) {
      return false; // safe on first load
    }
    return Object.keys(diffObjects(originalChart, chart)).length > 0;
  }, [originalChart, chart]);

  /**
   * Handles saving the chart, including validation and rule checks.
   * @param {Function} postChartUpdateFunction - Optional function to run
   * after saving.
   * @param {Function} postChartGetFunction - Optional function to run after
   * chart get completes.
   */
  const handleSaveChart = async (
    postChartUpdateFunction,
    postChartGetFunction,
  ) => {
    setIsUpdating(true);
    const validationIssues = validateChartFields(validationRules);
    if (Object.keys(validationIssues).length === 0) {
      // If there are pending changes and a postChartUpdateFunction, show dialog
      // JZ 2025-11-22 Removed this? It seems really strange to me that if I try to save the chart it tells me to save unsaved changes...
      // I think I did this for some flow with notes or something but this makes no sense to me right now and causes confusing issues.

      // SR 2025-12-11 I think this is needed for the insurance eligibility check flow to work correctly...
      // Otherwise the chart will automatically save (without notifying users) when they go add a note.
      // Plus, I need the dialog to open to notify users of unsaved changes if Check Eligibility is clicked.
      if (hasPendingChanges && postChartUpdateFunction) {
        setPendingSave(() => postChartUpdateFunction);
        setShowSaveDialog(true);
        setIsUpdating(false);
        return;
      }
      await proceedSave(postChartUpdateFunction, postChartGetFunction);
      setIsUpdating(false);
    } else {
      NotistackEnqueueSnackbar("Please correct the displayed issues.", {
        variant: "error",
      });
      setIsUpdating(false);
    }
  };

  // Save logic extracted for reuse after dialog confirm
  const proceedSave = async (postChartUpdateFunction, postChartGetFunction) => {
    // Run the rules engine on orders. If there violations, immediately fail.
    if (orderId && orderId > 0) {
      const ruleResults = RuleEngine.evaluateAll(
        originalChart,
        chart,
        orderId,
        "order_activity",
      );
      const ruleViolations = Object.keys(ruleResults).reduce(
        (accumulator, key) => {
          if (ruleResults[key] && ruleResults[key].passed === false) {
            accumulator[key] = ruleResults[key];
          }
          return accumulator;
        },
        {},
      );
      setRuleViolations(ruleViolations);
      if (Object.keys(ruleViolations).length > 0) {
        NotistackEnqueueSnackbar("One or more violations present.", {
          variant: "error",
        });
        setIsUpdating(false);
        return;
      }
    } else {
      setRuleViolations({});
    }

    if (!hasPendingChanges) {
      // No pending changes, just run postChartUpdateFunction if provided
      if (postChartUpdateFunction) {
        try {
          await postChartUpdateFunction();
        } catch (e) {
          // Ignore error
        }
        // Always fetch chart after postChartUpdateFunction
        try {
          const getChartResponse = await api.send("POST", `/chart/get`, {
            patientId: parseInt(patientId),
          });
          setOriginalChart(cloneChartData(getChartResponse));
          setChart(cloneChartData(getChartResponse));
          if (postChartGetFunction) {
            await postChartGetFunction();
          }
        } catch (e) {
          console.error("Error fetching chart after update:", e);
          NotistackEnqueueSnackbar("Error fetching chart after update", {
            variant: "error",
          });
        }
      }
      setValidationIssues({});
      return;
    }
    setIsUpdating(true);
    const updateData = diffObjects(originalChart, chart);

    // Field renaming configuration
    const fieldRenameMap = {
      deliveryInformation: {
        documentInfoPruned: "documentInfo",
      },
    };

    // Apply field renames
    for (const table in fieldRenameMap) {
      if (updateData[table]) {
        const renameRules = fieldRenameMap[table];
        for (const id in updateData[table]) {
          for (const oldField in renameRules) {
            if (updateData[table][id][oldField] !== undefined) {
              const newField = renameRules[oldField];
              updateData[table][id][newField] = updateData[table][id][oldField];
              delete updateData[table][id][oldField];
            }
          }
        }
      }
    }

    const chartUpdateParameters = {
      patientId: parseInt(patientId),
      updateData: updateData,
      returnChart: !postChartUpdateFunction,
    };
    try {
      const apiChart = await api.send(
        "POST",
        `/chart/update`,
        chartUpdateParameters,
        0,
      );

      NotistackEnqueueSnackbar("Changes saved", { variant: "success" });
      if (postChartUpdateFunction) {
        try {
          await postChartUpdateFunction();
        } catch (e) {
          // Ignore error
        }
        try {
          const getChartResponse = await api.send("POST", `/chart/get`, {
            patientId: parseInt(patientId),
          });
          setOriginalChart(cloneChartData(getChartResponse));
          setChart(cloneChartData(getChartResponse));
          if (postChartGetFunction) {
            await postChartGetFunction();
          }
        } catch (e) {
          console.error(e);
          NotistackEnqueueSnackbar("Error fetching chart after update", {
            variant: "error",
          });
        }
      } else {
        setOriginalChart(cloneChartData(apiChart));
        setChart(cloneChartData(apiChart));
        setValidationRules({});
        setValidationIssues({});
        setCreateTicketValidationRules({});
        setCreateTicketValidationIssues({});
        window.salesPilot.negativeId = -1;

        if (
          ORDER_PAGE_MODES.includes(pageMode) ||
          TICKET_PAGE_MODES.includes(pageMode)
        ) {
          const lastOrderId =
            apiChart.orderActivity[
              Object.keys(apiChart.orderActivity)[
                Object.keys(apiChart.orderActivity).length - 1
              ]
            ].id;
          setDraftOrderState(null);
          history.push(`/chart/${patientId}/order/${lastOrderId}`);
        }

        if (postChartGetFunction) {
          await postChartGetFunction();
        }
      }
      setIsUpdating(false);
    } catch (error) {
      console.error(error);

      if (error?.message?.includes("ORDER_CONFLICT:")) {
        NotistackEnqueueSnackbar(
          "This order was recently updated. Please refresh to see the latest changes.",
          { variant: "warning" },
        );
      } else {
        NotistackEnqueueSnackbar("Error saving changes", { variant: "error" });
      }

      setIsUpdating(false);
    }
    setValidationIssues({});
  };

  /**
   * Handles navigation with unsaved changes check. If there are pending
   * changes, prompt user to save before navigating.
   * @param {string} path - The path to navigate to.
   * @param {Object|null} state - Optional route state to preserve with navigation.
   */
  const handleNavigateChart = useCallback(
    (path, state = null) => {
      const navigationTarget = state ? { pathname: path, state } : path;

      if (hasPendingChanges) {
        setPendingNavigatePath(navigationTarget);
        setShowSaveDialog(true);
      } else {
        history.push(navigationTarget);
      }
    },
    [hasPendingChanges, history],
  );

  /**
   * Prepares the ticket data for the create ticket page.
   * @returns {Object} The prepareTicketData function.
   */
  const { prepareTicketData } = useChartCreateTicketFlow({
    chart: chart,
    patient: patient,
    patientId: patientId,
    mainContext: mainContext,
    formatOrderType: formatOrderType,
    onChange: onChange,
    getNegativeId: getNegativeId,
    orderId: orderId,
    setOrderId: setDraftOrderIdForCurrentRoute,
    pageMode: pageMode,
    isChartLoading: isChartLoading,
    location: location,
    history: history,
    ticketPageModes: TICKET_PAGE_MODES,
    routeParentOrderId: routeState.parentOrderId,
    handleNavigateChart: handleNavigateChart,
    hasPendingChanges: hasPendingChanges,
    setShowSaveDialog: setShowSaveDialog,
    setPendingNavigatePath: setPendingNavigatePath,
  });

  /**
   * Prepares the order data for the create order page.
   * @returns {Object} The prepareOrderData function.
   */
  const { prepareOrderData } = useChartCreateOrderFlow({
    chart: chart,
    patient: patient,
    patientId: patientId,
    mainContext: mainContext,
    formatOrderType: formatOrderType,
    onChange: onChange,
    getNegativeId: getNegativeId,
    orderId: orderId,
    setOrderId: setDraftOrderIdForCurrentRoute,
    pageMode: pageMode,
    isChartLoading: isChartLoading,
    orderPageModes: ORDER_PAGE_MODES,
    handleNavigateChart: handleNavigateChart,
    hasPendingChanges: hasPendingChanges,
    setShowSaveDialog: setShowSaveDialog,
    setPendingNavigatePath: setPendingNavigatePath,
    routeParentOrderId: routeState.parentOrderId,
  });

  /**
   * Requests the scroll to card.
   * @param {Object} request - The request object.
   * @returns {void}
   */
  const requestScrollToCard = useCallback((request) => {
    setRequestedScrollToCard(request || null);
  }, []);

  /**
   * Clears the requested scroll to card.
   * @returns {void}
   */
  const clearRequestedScrollToCard = useCallback(() => {
    setRequestedScrollToCard(null);
  }, []);

  /**
   * The context value.
   * @returns {Object} The context value.
   */
  const contextValue = useMemo(
    () => ({
      originalChart,
      chart,
      patientId,
      validationIssues,
      cardsInEditMode,
      setCardEditMode,
      handleSaveChart,
      onChange,
      isUpdating,
      updateValidationIssues,
      updateValidationRules,
      validationRules,
      orderId,
      expandedRows,
      updateExpandedRow,
      hasPendingChanges,
      ruleViolations,
      getNegativeId,
      setShowSaveDialog,
      handleNavigateChart,
      pageMode,
      createTicketValidationRules,
      setCreateTicketValidationRules,
      createTicketValidationIssues,
      setCreateTicketValidationIssues,
      resetChart,
      reloadChartData,
      prepareTicketData,
      pendingCreateTicket,
      setPendingCreateTicket,
      prepareOrderData,
      chartViews,
      setChartViews,
      selectedChartViewId,
      setSelectedChartViewId,
      validateChartFields,
      showManageSignatureDialog,
      setShowManageSignatureDialog,
      updateShowManageSignatureDialog,
      uploadedFile,
      setUploadedFile,
      requestedScrollToCard,
      requestScrollToCard,
      clearRequestedScrollToCard,
      insuranceDiscoveryActiveStep,
      updateInsuranceDiscoveryActiveStep,
      insuranceDiscoveryApiResults,
      updateInsuranceDiscoveryApiResults,
      insuranceDiscoverySelectedUser,
      insuranceDiscoverySelectedKey,
      updateInsuranceDiscoverySelectedUser,
      insuranceDiscoverySelectedPatientIndex,
      updateInsuranceDiscoverySelectedPatientIndex,
      insuranceDiscoveryCoverageTypes,
      updateInsuranceDiscoveryCoverageTypes,
      insuranceDiscoveryInsuranceOverrides,
      updateInsuranceDiscoveryInsuranceOverrides,
      insuranceDiscoveryIsLoading,
      updateInsuranceDiscoveryIsLoading,
      insuranceDiscoveryAddressChanges,
      updateInsuranceDiscoveryAddressChanges,
    }),
    [
      originalChart,
      chart,
      patientId,
      validationIssues,
      cardsInEditMode,
      setCardEditMode,
      handleSaveChart,
      onChange,
      updateValidationIssues,
      updateValidationRules,
      validationRules,
      isUpdating,
      orderId,
      expandedRows,
      updateExpandedRow,
      hasPendingChanges,
      ruleViolations,
      getNegativeId,
      setShowSaveDialog,
      pageMode,
      createTicketValidationRules,
      setCreateTicketValidationRules,
      createTicketValidationIssues,
      setCreateTicketValidationIssues,
      resetChart,
      prepareTicketData,
      pendingCreateTicket,
      setPendingCreateTicket,
      prepareOrderData,
      chartViews,
      setChartViews,
      selectedChartViewId,
      setSelectedChartViewId,
      validateChartFields,
      showManageSignatureDialog,
      setShowManageSignatureDialog,
      updateShowManageSignatureDialog,
      uploadedFile,
      setUploadedFile,
      requestedScrollToCard,
      requestScrollToCard,
      clearRequestedScrollToCard,
      insuranceDiscoveryActiveStep,
      updateInsuranceDiscoveryActiveStep,
      insuranceDiscoveryApiResults,
      updateInsuranceDiscoveryApiResults,
      insuranceDiscoverySelectedUser,
      insuranceDiscoverySelectedKey,
      updateInsuranceDiscoverySelectedUser,
      insuranceDiscoverySelectedPatientIndex,
      updateInsuranceDiscoverySelectedPatientIndex,
      insuranceDiscoveryCoverageTypes,
      updateInsuranceDiscoveryCoverageTypes,
      insuranceDiscoveryInsuranceOverrides,
      updateInsuranceDiscoveryInsuranceOverrides,
      insuranceDiscoveryIsLoading,
      updateInsuranceDiscoveryIsLoading,
      insuranceDiscoveryAddressChanges,
    ],
  );

  return (
    <>
      <ChartContext.Provider value={contextValue}>
        {children}
      </ChartContext.Provider>
      <MUIDialog
        open={showSaveDialog}
        onClose={() => {
          setShowSaveDialog(false);
          setPendingNavigatePath(null);
          setPendingSave(null);
        }}
      >
        <MUIDialogTitle>Save Changes</MUIDialogTitle>
        <MUIDialogContent>
          You have unsaved changes; please save before continuing.
        </MUIDialogContent>
        <MUIDialogActions>
          <MUIButton
            variant="text"
            color="error"
            onClick={() => {
              resetChart();
              setShowSaveDialog(false);
              // After discarding, continue navigation if there was a pending path
              if (pendingNavigatePath) {
                history.push(pendingNavigatePath);
                setPendingNavigatePath(null);
              }
              setPendingSave(null);
            }}
          >
            Discard Changes
          </MUIButton>
          <MUIButton
            variant="contained"
            color="primary"
            onClick={async () => {
              setShowSaveDialog(false);
              const validationIssues = validateChartFields(validationRules);
              if (Object.keys(validationIssues).length > 0) {
                NotistackEnqueueSnackbar(
                  "Please correct the displayed issues.",
                  { variant: "error" },
                );
                return;
              } else {
                if (pendingSave) {
                  await proceedSave(pendingSave);
                  setPendingSave(null);
                }
                if (pendingNavigatePath) {
                  await proceedSave(); // save before navigating
                  history.push(pendingNavigatePath);
                  setPendingNavigatePath(null);
                }
              }
            }}
            autoFocus
          >
            Save & Continue
          </MUIButton>
        </MUIDialogActions>
      </MUIDialog>
      <MUIBackdrop open={isUpdating}>
        <Progress />
      </MUIBackdrop>
    </>
  );
}
